import _ from 'lodash';
import React from 'react';
import { LuPlus } from 'react-icons/lu';
import { toast } from 'sonner';

import { CashFlowConfig, CashFlowConfigSaveRequest, CashFlowType, LifecycleStatus } from '@ndvr/portfolio-api';

import { ConfirmModal } from '@/components/ConfirmModal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRequiredParams } from 'utils';
import { logger } from 'utils/logger';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useDeleteCashflowConfig, useUpdateCashflowConfig } from 'views/portfolio/account-group-queries';
import { useCreateCashFlowConfig } from 'views/portfolio/cash-flow-queries';
import {
    convertCashFlowConfigToForm,
    convertFormToCashFlowConfigSaveRequest,
} from 'views/portfolio/PortfolioPage/utils/FormCashFlowConfigConverter';
import { Toast } from 'widgets';

import { CashflowTable } from './CashflowTable';
import { CashflowStream, EditCashflowDialog } from './EditCashflowDialog';
import { LiquidityPlanTimelineChart } from './LiquidityPlanTimelineChart';
import { useLiquidityPlanningData } from './useLiquidityPlanningData';

const emptyForm: CashflowStream = {
    id: '',
    name: '',
    type: 'withdrawal' as const,
    frequency: 'one-time' as const,
    amount: 0,
    startDate: new Date().toISOString().split('T')[0],
    adjustForInflation: false,
    inflationRate: 2.4,
    account: 'portfolio_level' as const,
    repeatUntil: 'cancel' as const,
};

export const LiquidityPlanningTab = () => {
    const { accountGroupGUID, globalAccountGUID } = useRequiredParams(['globalAccountGUID', 'accountGroupGUID']);
    const {
        isLoading: isCashflowConfigsLoading,
        cashFlowConfigs,
        accounts,
    } = useLiquidityPlanningData(accountGroupGUID, globalAccountGUID);

    const { data: accountGroupProfile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    const { mutateAsync: createCashflowConfig, isPending: isCreatingCashflowConfig } = useCreateCashFlowConfig();

    const { mutateAsync: updateCashflowConfig, isPending: isUpdatingCashflowConfig } =
        useUpdateCashflowConfig(accountGroupGUID);

    const { mutateAsync: deleteCashflowConfig, isPending: isDeletingCashflowConfig } =
        useDeleteCashflowConfig(accountGroupGUID);

    const isLoading = isCashflowConfigsLoading;

    const [cashFlowType, setCashFlowType] = React.useState<string>('all');
    const [showPastCashFlows, setShowPastCashFlows] = React.useState<boolean>(false);
    const [showAddDialog, setShowAddDialog] = React.useState<boolean>(false);
    const [newCashflow, setNewCashflow] = React.useState(emptyForm);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState<boolean>(false);
    const [cashflowToDelete, setCashflowToDelete] = React.useState<CashFlowConfig | null>(null);

    const handleAddCashflow = () => {
        // Set default type based on selected tab
        const defaultType = cashFlowType === 'contributions' ? 'contribution' : 'withdrawal';

        setNewCashflow({
            ...emptyForm,
            type: defaultType,
        });
        setShowAddDialog(true);
    };

    const handleSaveCashflow = (updatedCashflow: any) => {
        logger.debug('Saving new cashflow, form data:', updatedCashflow);

        const cashFlowConfigSaveRequest: CashFlowConfigSaveRequest = convertFormToCashFlowConfigSaveRequest(
            globalAccountGUID,
            updatedCashflow,
        );

        if (!updatedCashflow.id) {
            createCashflowConfig({ accountGroupGUID, cashFlowConfig: cashFlowConfigSaveRequest })
                .then(() => toast.success('Cash flow created successfully.'))
                .catch((error) => Toast.forError(error, 'Failed to create cash flow config'));
        } else {
            updateCashflowConfig({ cashFlowConfigGUID: updatedCashflow.id, cashFlowConfigSaveRequest })
                .then(() => logger.debug('Successfully updated cashflow'))
                .catch(logger.error);
        }

        setShowAddDialog(false);
        setNewCashflow(emptyForm);
    };

    const handleCancelAdd = () => {
        setShowAddDialog(false);
        setNewCashflow(emptyForm);
    };

    const handleEdit = (cashflow: CashFlowConfig) => {
        const cashFlowStream: CashflowStream = convertCashFlowConfigToForm(cashflow);
        logger.debug({ cashFlowStream });
        setShowAddDialog(true);
        setNewCashflow(cashFlowStream);
    };

    const handleDelete = (cashflow: CashFlowConfig) => {
        setCashflowToDelete(cashflow);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (!cashflowToDelete) return;

        logger.debug('Deleting cashflow:', cashflowToDelete);
        deleteCashflowConfig({ cashFlowConfigGUID: cashflowToDelete.cashFlowConfigGUID })
            .then(() => logger.debug('Successfully deleted cashflow'))
            .catch(logger.error);

        setShowDeleteConfirm(false);
        setCashflowToDelete(null);
    };

    const handleCancelDelete = () => {
        setCashflowToDelete(null);
        setShowDeleteConfirm(false);
    };

    const filteredCashFlowConfigs = cashFlowConfigs?.filter((config) => {
        if (cashFlowType === 'all') return true;
        if (cashFlowType === 'contributions') return config.cashFlowType === CashFlowType.CONTRIBUTION;
        if (cashFlowType === 'withdrawals') return config.cashFlowType === CashFlowType.WITHDRAWAL;
        return false;
    });

    const sortedCashFlowConfigs = _.sortBy(
        filteredCashFlowConfigs,
        (config) => new Date(config.factorDateFrame.startDate),
    );

    // Get button text based on selected tab
    const getButtonText = () => {
        switch (cashFlowType) {
            case 'contributions':
                return 'Add Contribution';
            case 'withdrawals':
                return 'Add Withdrawal';
            default:
                return 'Add Cashflow';
        }
    };

    const isDraftAccountGroup = accountGroupProfile?.accountGroup.lifecycleStatus === LifecycleStatus.DRAFT;

    return (
        <div>
            <div className="flex items-start justify-between">
                <h2 className="text-cocoa-1000 text-xl">Cashflow Streams</h2>
                <Button
                    className="flex items-center gap-2 text-white hover:opacity-90"
                    style={{ backgroundColor: 'rgb(125, 85, 43)' }}
                    onClick={handleAddCashflow}
                >
                    <LuPlus className="h-4 w-4" />
                    {getButtonText()}
                </Button>
            </div>
            <div className="relative">
                <div className="mt-2 flex flex-col gap-6">
                    <div className="flex items-center justify-between">
                        <Tabs defaultValue="all" value={cashFlowType} onValueChange={setCashFlowType}>
                            <TabsList className="border-none">
                                <TabsTrigger value="all">All</TabsTrigger>
                                <TabsTrigger value="contributions">Contributions</TabsTrigger>
                                <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <div className="flex items-center space-x-2">
                            <Switch
                                id="show-past-cashflows"
                                checked={showPastCashFlows}
                                onCheckedChange={setShowPastCashFlows}
                            />
                            <Label htmlFor="show-past-cashflows" className="text-sm">
                                Show past cash flows
                            </Label>
                        </div>
                    </div>

                    <Card>
                        <CashflowTable
                            accounts={accounts || []}
                            streams={sortedCashFlowConfigs || []}
                            showType={cashFlowType === 'all'}
                            showPastCashFlows={showPastCashFlows}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            isLoading={isLoading}
                        />
                    </Card>
                </div>
            </div>
            <div className="mt-8">
                <LiquidityPlanTimelineChart
                    accountGroupGUID={accountGroupGUID}
                    isDraftAccountGroup={isDraftAccountGroup}
                />
            </div>

            <EditCashflowDialog
                open={showAddDialog}
                cashflow={newCashflow}
                onSave={handleSaveCashflow}
                onCancel={handleCancelAdd}
                accounts={accounts || []}
            />

            <ConfirmModal
                open={showDeleteConfirm}
                onOpenChange={setShowDeleteConfirm}
                title="Delete Cashflow Stream"
                description={`Are you sure you want to delete the cashflow stream "${cashflowToDelete?.title?.plainText || 'Unnamed'}"? This action cannot be undone.`}
                confirmText="Delete"
                confirmVariant="destructive"
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                loading={isDeletingCashflowConfig}
            />
        </div>
    );
};
